import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useBluelagoon } from "@bluelagoon";
import ServiceCard from "./ServiceCard";
import UptimeBar from "./UptimeBar";
import IncidentList from "./IncidentList";
import StatusHeader from "./StatusHeader";

const REFRESH_INTERVAL = 60_000;

function StatusPage() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);
    const { call: fetchStatus, isLoading: loading } = useBluelagoon({
        method: "getPublicStatus",
    });

    const loadStatus = useCallback(async () => {
        try {
            const res = await fetchStatus({});
            setData(res?.data || res);
            setLastRefresh(new Date());
            setError(null);
        } catch (err) {
            setError(t("statusPage.fetchError"));
        }
    }, [fetchStatus, t]);

    useEffect(() => {
        loadStatus();
        const interval = setInterval(loadStatus, REFRESH_INTERVAL);
        return () => clearInterval(interval);
    }, [loadStatus]);

    const overall = data?.current?.overall || "operational";
    const services = data?.current?.services || [];
    const history = data?.history || [];
    const incidents = data?.incidents || [];

    return (
        <div className="min-h-screen bg-gray-50">
            <Helmet><title>{t("statusPage.title")} - AgentUI</title></Helmet>
            <div className="max-w-2xl mx-auto px-4 py-12 space-y-8">
                <StatusHeader overall={overall} loading={loading} error={error} lastRefresh={lastRefresh} />
                <div className="space-y-2">
                    {services.map((service) => (
                        <ServiceCard key={service.name} service={service} />
                    ))}
                    {!loading && services.length === 0 && (
                        <ServiceCard service={{ name: "API", status: "operational" }} />
                    )}
                </div>
                <div className="p-5 rounded-xl border border-gray-100 bg-white">
                    <UptimeBar history={history} />
                </div>
                <div className="space-y-3">
                    <h3 className="text-sm font-medium text-gray-700">
                        {t("statusPage.incidentsTitle")}
                    </h3>
                    <IncidentList incidents={incidents} />
                </div>
                <p className="text-center text-xs text-gray-400">
                    {t("statusPage.autoRefresh")}
                </p>
            </div>
        </div>
    );
}

export default StatusPage;
